/**
 * Enhanced Performance Monitor
 * GPU使用率、メモリ使用量、フレームタイムを詳細に監視
 */

import * as THREE from 'three'
import { webglLogger } from '@/lib/utils/logger'
import type { PerformanceMetrics, DeviceCapabilities } from './AdaptiveQualitySystem'

export interface PerformanceThresholds {
  /** 警告FPS閾値 */
  warningFPS: number
  /** 危険FPS閾値 */
  criticalFPS: number
  /** 最大フレーム時間 (ms) */
  maxFrameTime: number
  /** 最大メモリ使用量 (MB) */
  maxMemoryUsage: number
  /** 最大描画コール数 */
  maxDrawCalls: number
}

export interface PerformanceAlert {
  type: 'warning' | 'critical'
  metric: keyof PerformanceMetrics
  value: number
  threshold: number
  timestamp: number
  message: string
}

export class EnhancedPerformanceMonitor {
  private renderer: THREE.WebGLRenderer
  private isMonitoring: boolean = false
  private frameCount: number = 0
  private lastTime: number = 0
  private fpsHistory: number[] = []
  private frameTimeHistory: number[] = []
  private memoryHistory: number[] = []
  private drawCallHistory: number[] = []
  private triangleHistory: number[] = []
  
  private currentMetrics: PerformanceMetrics
  private thresholds: PerformanceThresholds
  private alerts: PerformanceAlert[] = []
  private callbacks: Map<string, (metrics: PerformanceMetrics) => void> = new Map()
  private alertCallbacks: Map<string, (alert: PerformanceAlert) => void> = new Map()
  
  // GPU拡張機能
  private timerExt: unknown
  private memoryExt: unknown
  private debugExt: unknown
  
  constructor(
    renderer: THREE.WebGLRenderer,
    thresholds: Partial<PerformanceThresholds> = {}
  ) {
    this.renderer = renderer
    this.thresholds = {
      warningFPS: 45,
      criticalFPS: 30,
      maxFrameTime: 20,
      maxMemoryUsage: 512,
      maxDrawCalls: 500,
      ...thresholds
    }
    
    this.currentMetrics = {
      fps: 0,
      frameTime: 0,
      gpuUtilization: 0,
      memoryUsage: 0,
      drawCalls: 0,
      triangles: 0,
      textureMemory: 0
    }
    
    this.initializeExtensions()
    
    webglLogger.info('[EnhancedPerformanceMonitor] Initialized', {
      thresholds: this.thresholds
    })
  }

  /**
   * WebGL拡張機能を初期化
   */
  private initializeExtensions(): void {
    const gl = this.renderer.getContext()
    
    // GPUタイマー拡張（Chrome/Firefox）
    this.timerExt = gl.getExtension('EXT_disjoint_timer_query_webgl2') ||
                   gl.getExtension('EXT_disjoint_timer_query')
    
    // メモリ情報拡張（Chrome）
    this.memoryExt = gl.getExtension('WEBGL_debug_renderer_info')
    
    // デバッグ拡張
    this.debugExt = gl.getExtension('WEBGL_debug_shaders')
    
    webglLogger.info('[EnhancedPerformanceMonitor] Extensions available:', {
      timer: !!this.timerExt,
      memory: !!this.memoryExt,
      debug: !!this.debugExt
    })
  }

  /**
   * 監視を開始
   */
  public start(): void {
    if (this.isMonitoring) return
    
    this.isMonitoring = true
    this.lastTime = performance.now()
    this.frameCount = 0
    
    webglLogger.info('[EnhancedPerformanceMonitor] Monitoring started')
  }

  /**
   * 監視を停止
   */
  public stop(): void {
    this.isMonitoring = false
    webglLogger.info('[EnhancedPerformanceMonitor] Monitoring stopped')
  }

  /**
   * フレーム毎の更新（レンダーループで呼び出し）
   */
  public update(): void {
    if (!this.isMonitoring) return
    
    const now = performance.now()
    this.frameCount++
    
    // FPS計算（1秒毎）
    if (now - this.lastTime >= 1000) {
      const fps = (this.frameCount * 1000) / (now - this.lastTime)
      this.updateFPS(fps)
      
      this.frameCount = 0
      this.lastTime = now
    }
    
    // フレーム時間
    const frameTime = now - (this.lastFrameTime || now)
    this.lastFrameTime = now
    this.updateFrameTime(frameTime)
    
    // レンダラー情報
    this.updateRendererInfo()
    
    // メモリ使用量
    this.updateMemoryUsage()
    
    // GPU使用率（推定）
    this.updateGPUUtilization()
    
    // メトリクスを更新
    this.currentMetrics = {
      fps: this.getAverageFPS(),
      frameTime: this.getAverageFrameTime(),
      gpuUtilization: this.estimateGPUUtilization(),
      memoryUsage: this.getCurrentMemoryUsage(),
      drawCalls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      textureMemory: this.estimateTextureMemory()
    }
    
    // 閾値チェック
    this.checkThresholds()
    
    // コールバック実行
    this.callbacks.forEach(callback => {
      try {
        callback(this.currentMetrics)
      } catch (error) {
        webglLogger.error('[EnhancedPerformanceMonitor] Callback error:', error)
      }
    })
  }

  private lastFrameTime?: number

  /**
   * FPS履歴を更新
   */
  private updateFPS(fps: number): void {
    this.fpsHistory.push(fps)
    if (this.fpsHistory.length > 60) { // 1分間の履歴
      this.fpsHistory.shift()
    }
  }

  /**
   * フレーム時間履歴を更新
   */
  private updateFrameTime(frameTime: number): void {
    this.frameTimeHistory.push(frameTime)
    if (this.frameTimeHistory.length > 120) { // 2秒間の履歴
      this.frameTimeHistory.shift()
    }
  }

  /**
   * レンダラー情報を更新
   */
  private updateRendererInfo(): void {
    const info = this.renderer.info
    
    this.drawCallHistory.push(info.render.calls)
    this.triangleHistory.push(info.render.triangles)
    
    if (this.drawCallHistory.length > 60) {
      this.drawCallHistory.shift()
    }
    if (this.triangleHistory.length > 60) {
      this.triangleHistory.shift()
    }
  }

  /**
   * メモリ使用量を更新
   */
  private updateMemoryUsage(): void {
    let memoryUsage = 0
    
    // パフォーマンス.メモリが利用可能な場合
    if ((performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory) {
      const memory = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory!
      memoryUsage = memory.usedJSHeapSize / (1024 * 1024) // MB
    }
    
    this.memoryHistory.push(memoryUsage)
    if (this.memoryHistory.length > 60) {
      this.memoryHistory.shift()
    }
  }

  /**
   * GPU使用率を更新（推定）
   */
  private updateGPUUtilization(): void {
    // GPUタイマーが利用可能な場合の処理を実装
    // 現在は描画コール数とフレーム時間から推定
  }

  /**
   * 平均FPSを取得
   */
  private getAverageFPS(): number {
    if (this.fpsHistory.length === 0) return 0
    return this.fpsHistory.reduce((sum, fps) => sum + fps, 0) / this.fpsHistory.length
  }

  /**
   * 平均フレーム時間を取得
   */
  private getAverageFrameTime(): number {
    if (this.frameTimeHistory.length === 0) return 0
    const recent = this.frameTimeHistory.slice(-30) // 最新30フレーム
    return recent.reduce((sum, time) => sum + time, 0) / recent.length
  }

  /**
   * GPU使用率を推定
   */
  private estimateGPUUtilization(): number {
    const frameTime = this.getAverageFrameTime()
    const drawCalls = this.renderer.info.render.calls
    
    // 16.67ms (60fps) を基準とした簡易推定
    const baselineFrameTime = 16.67
    const frameTimeRatio = Math.min(frameTime / baselineFrameTime, 1.0)
    
    // 描画コール数による重み付け
    const drawCallWeight = Math.min(drawCalls / 100, 1.0)
    
    return Math.min((frameTimeRatio * 0.7 + drawCallWeight * 0.3) * 100, 100)
  }

  /**
   * 現在のメモリ使用量を取得
   */
  private getCurrentMemoryUsage(): number {
    if (this.memoryHistory.length === 0) return 0
    return this.memoryHistory[this.memoryHistory.length - 1]
  }

  /**
   * テクスチャメモリを推定
   */
  private estimateTextureMemory(): number {
    const info = this.renderer.info
    // 簡易推定：テクスチャ数 × 平均サイズ
    return info.memory.textures * 2 // MB
  }

  /**
   * 閾値チェックとアラート生成
   */
  private checkThresholds(): void {
    const metrics = this.currentMetrics
    const now = performance.now()
    
    // FPS警告
    if (metrics.fps < this.thresholds.criticalFPS) {
      this.addAlert({
        type: 'critical',
        metric: 'fps',
        value: metrics.fps,
        threshold: this.thresholds.criticalFPS,
        timestamp: now,
        message: `Critical FPS drop: ${metrics.fps.toFixed(1)} < ${this.thresholds.criticalFPS}`
      })
    } else if (metrics.fps < this.thresholds.warningFPS) {
      this.addAlert({
        type: 'warning',
        metric: 'fps',
        value: metrics.fps,
        threshold: this.thresholds.warningFPS,
        timestamp: now,
        message: `FPS warning: ${metrics.fps.toFixed(1)} < ${this.thresholds.warningFPS}`
      })
    }
    
    // フレーム時間警告
    if (metrics.frameTime > this.thresholds.maxFrameTime) {
      this.addAlert({
        type: 'warning',
        metric: 'frameTime',
        value: metrics.frameTime,
        threshold: this.thresholds.maxFrameTime,
        timestamp: now,
        message: `High frame time: ${metrics.frameTime.toFixed(1)}ms > ${this.thresholds.maxFrameTime}ms`
      })
    }
    
    // メモリ警告
    if (metrics.memoryUsage > this.thresholds.maxMemoryUsage) {
      this.addAlert({
        type: 'warning',
        metric: 'memoryUsage',
        value: metrics.memoryUsage,
        threshold: this.thresholds.maxMemoryUsage,
        timestamp: now,
        message: `High memory usage: ${metrics.memoryUsage.toFixed(1)}MB > ${this.thresholds.maxMemoryUsage}MB`
      })
    }
    
    // 描画コール警告
    if (metrics.drawCalls > this.thresholds.maxDrawCalls) {
      this.addAlert({
        type: 'warning',
        metric: 'drawCalls',
        value: metrics.drawCalls,
        threshold: this.thresholds.maxDrawCalls,
        timestamp: now,
        message: `High draw calls: ${metrics.drawCalls} > ${this.thresholds.maxDrawCalls}`
      })
    }
  }

  /**
   * アラートを追加
   */
  private addAlert(alert: PerformanceAlert): void {
    this.alerts.push(alert)
    
    // アラート履歴を制限（最新100件）
    if (this.alerts.length > 100) {
      this.alerts.shift()
    }
    
    // アラートコールバック実行
    this.alertCallbacks.forEach(callback => {
      try {
        callback(alert)
      } catch (error) {
        webglLogger.error('[EnhancedPerformanceMonitor] Alert callback error:', error)
      }
    })
    
    // ログ出力
    const level = alert.type === 'critical' ? 'error' : 'warn'
    webglLogger[level]('[PerformanceAlert]', alert.message, alert)
  }

  /**
   * 現在のメトリクスを取得
   */
  public getCurrentMetrics(): PerformanceMetrics {
    return { ...this.currentMetrics }
  }

  /**
   * パフォーマンス履歴を取得
   */
  public getHistory(): {
    fps: number[]
    frameTime: number[]
    memory: number[]
    drawCalls: number[]
    triangles: number[]
  } {
    return {
      fps: [...this.fpsHistory],
      frameTime: [...this.frameTimeHistory],
      memory: [...this.memoryHistory],
      drawCalls: [...this.drawCallHistory],
      triangles: [...this.triangleHistory]
    }
  }

  /**
   * アラート履歴を取得
   */
  public getAlerts(type?: 'warning' | 'critical'): PerformanceAlert[] {
    if (type) {
      return this.alerts.filter(alert => alert.type === type)
    }
    return [...this.alerts]
  }

  /**
   * パフォーマンス統計を取得
   */
  public getStatistics(): {
    fps: { min: number, max: number, avg: number }
    frameTime: { min: number, max: number, avg: number }
    memory: { min: number, max: number, avg: number }
  } {
    return {
      fps: this.calculateStats(this.fpsHistory),
      frameTime: this.calculateStats(this.frameTimeHistory),
      memory: this.calculateStats(this.memoryHistory)
    }
  }

  /**
   * 統計を計算
   */
  private calculateStats(data: number[]): { min: number, max: number, avg: number } {
    if (data.length === 0) {
      return { min: 0, max: 0, avg: 0 }
    }
    
    const min = Math.min(...data)
    const max = Math.max(...data)
    const avg = data.reduce((sum, val) => sum + val, 0) / data.length
    
    return { min, max, avg }
  }

  /**
   * メトリクス更新コールバックを登録
   */
  public onMetricsUpdate(id: string, callback: (metrics: PerformanceMetrics) => void): void {
    this.callbacks.set(id, callback)
  }

  /**
   * アラートコールバックを登録
   */
  public onAlert(id: string, callback: (alert: PerformanceAlert) => void): void {
    this.alertCallbacks.set(id, callback)
  }

  /**
   * コールバックを削除
   */
  public removeCallback(id: string): void {
    this.callbacks.delete(id)
    this.alertCallbacks.delete(id)
  }

  /**
   * 閾値を更新
   */
  public updateThresholds(thresholds: Partial<PerformanceThresholds>): void {
    this.thresholds = { ...this.thresholds, ...thresholds }
    webglLogger.info('[EnhancedPerformanceMonitor] Thresholds updated', this.thresholds)
  }

  /**
   * 履歴をクリア
   */
  public clearHistory(): void {
    this.fpsHistory.length = 0
    this.frameTimeHistory.length = 0
    this.memoryHistory.length = 0
    this.drawCallHistory.length = 0
    this.triangleHistory.length = 0
    this.alerts.length = 0
    
    webglLogger.info('[EnhancedPerformanceMonitor] History cleared')
  }

  /**
   * レンダラー情報をリセット
   */
  public resetRendererInfo(): void {
    this.renderer.info.reset()
  }

  /**
   * システムを破棄
   */
  public dispose(): void {
    this.stop()
    this.callbacks.clear()
    this.alertCallbacks.clear()
    this.clearHistory()
    
    webglLogger.info('[EnhancedPerformanceMonitor] Disposed')
  }
}

/**
 * パフォーマンス監視ファクトリー
 */
export function createEnhancedPerformanceMonitor(
  renderer: THREE.WebGLRenderer,
  deviceCapabilities: DeviceCapabilities,
  customThresholds?: Partial<PerformanceThresholds>
): EnhancedPerformanceMonitor {
  // デバイス性能に基づいて閾値を調整
  let thresholds: Partial<PerformanceThresholds>
  
  if (deviceCapabilities.isMobile || deviceCapabilities.gpuTier === 1) {
    thresholds = {
      warningFPS: 25,
      criticalFPS: 20,
      maxFrameTime: 40,
      maxMemoryUsage: 256,
      maxDrawCalls: 200
    }
  } else if (deviceCapabilities.gpuTier === 2) {
    thresholds = {
      warningFPS: 35,
      criticalFPS: 25,
      maxFrameTime: 25,
      maxMemoryUsage: 512,
      maxDrawCalls: 350
    }
  } else {
    thresholds = {
      warningFPS: 45,
      criticalFPS: 30,
      maxFrameTime: 20,
      maxMemoryUsage: 1024,
      maxDrawCalls: 500
    }
  }
  
  // カスタム閾値で上書き
  if (customThresholds) {
    thresholds = { ...thresholds, ...customThresholds }
  }
  
  return new EnhancedPerformanceMonitor(renderer, thresholds)
}